import { useState } from 'react';
import type { WeeklyEvolutionPoint, WeeklyMetric, Provenance } from '@losan/core';
import { DemoBadge } from './DemoBadge.js';

interface Props {
  points: WeeklyEvolutionPoint[];
  provenance: Provenance;
  currentSeason: string;
  previousSeason: string | null;
}

const eurFmt = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const intFmt = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });

const METRICS: Array<{ id: WeeklyMetric; label: string }> = [
  { id: 'revenue', label: 'Facturación' },
  { id: 'units',   label: 'Unidades' },
  { id: 'clients', label: 'Nº clientes' },
];

/** Pull the right triple (actual / last / target) for a point given the active metric. */
function valuesFor(p: WeeklyEvolutionPoint, metric: WeeklyMetric) {
  switch (metric) {
    case 'revenue': return { actual: p.revenueActual, last: p.revenueLastSeason, target: p.revenueTarget };
    case 'units':   return { actual: p.unitsActual,   last: p.unitsLastSeason,   target: p.unitsTarget };
    case 'clients': return { actual: p.clientsActual, last: p.clientsLastSeason, target: p.clientsTarget };
  }
}

function format(v: number, metric: WeeklyMetric): string {
  return metric === 'revenue' ? eurFmt.format(v) : intFmt.format(v);
}

export function WeeklyEvolutionChart({ points, provenance, currentSeason, previousSeason }: Props) {
  const [metric, setMetric] = useState<WeeklyMetric>('revenue');
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (points.length === 0) {
    return (
      <div className="section weekly">
        <h2>Evolución semanal <DemoBadge provenance={provenance} /></h2>
        <div className="empty-hint">No hay ventas que mostrar con los filtros actuales.</div>
      </div>
    );
  }

  // Compute scale.
  const triples = points.map((p) => valuesFor(p, metric));
  const max = Math.max(
    1,
    ...triples.flatMap((t) => [t.actual, t.last, t.target]),
  );

  // Layout.
  const width = 800;
  const height = 280;
  const padL = 56, padR = 16, padT = 18, padB = 36;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const colW = innerW / points.length;
  const barW = Math.max(10, colW * 0.55);

  const yFor = (v: number) => padT + innerH - (v / max) * innerH;

  // Y axis ticks (4).
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => max * t);

  return (
    <div className="section weekly">
      <header className="weekly-head">
        <h2>Evolución semanal · {currentSeason} <DemoBadge provenance={provenance} /></h2>
        <div className="metric-toggle" role="tablist">
          {METRICS.map((m) => (
            <button
              key={m.id}
              role="tab"
              aria-selected={metric === m.id}
              className={metric === m.id ? 'active' : ''}
              onClick={() => setMetric(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </header>

      <div className="weekly-legend">
        <span className="legend-item"><span className="legend-swatch swatch-actual" /> {currentSeason}</span>
        <span className="legend-item"><span className="legend-swatch swatch-last" /> {previousSeason ?? 'LY'}</span>
        <span className="legend-item"><span className="legend-swatch swatch-target" /> Objetivo</span>
      </div>

      <div className="weekly-svg-wrap">
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="Evolución semanal">
          {/* Y-axis gridlines */}
          {ticks.map((v, i) => (
            <g key={i}>
              <line x1={padL} y1={yFor(v)} x2={width - padR} y2={yFor(v)} className="grid" />
              <text x={padL - 6} y={yFor(v)} className="axis-y" textAnchor="end" dominantBaseline="middle">
                {format(v, metric)}
              </text>
            </g>
          ))}

          {points.map((p, i) => {
            const t = triples[i]!;
            const xCenter = padL + colW * (i + 0.5);
            const xBar = xCenter - barW / 2;
            const xLast = xCenter - colW * 0.18;
            const xTarget = xCenter + colW * 0.18;

            const hAct = Math.max(0, (t.actual / max) * innerH);
            const hover = hoverIdx === i;
            return (
              <g
                key={p.weekNumber}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              >
                {/* Hover hit-area (transparent) */}
                <rect x={padL + colW * i} y={padT} width={colW} height={innerH} fill="transparent" />
                {/* Current-season bar */}
                <rect
                  x={xBar}
                  y={padT + innerH - hAct}
                  width={barW}
                  height={hAct}
                  className={`bar ${hover ? 'hover' : ''}`}
                  rx="2"
                />
                {/* Last-season marker (short horizontal line) */}
                {t.last > 0 && (
                  <line
                    x1={xLast - colW * 0.18}
                    x2={xLast + colW * 0.18}
                    y1={yFor(t.last)}
                    y2={yFor(t.last)}
                    className="marker-last"
                  />
                )}
                {/* Target marker (chevron/short line) */}
                {t.target > 0 && (
                  <line
                    x1={xTarget - colW * 0.18}
                    x2={xTarget + colW * 0.18}
                    y1={yFor(t.target)}
                    y2={yFor(t.target)}
                    className="marker-target"
                  />
                )}
                {/* X-axis label (sparse: only every other week if many) */}
                {(points.length <= 18 || i % 2 === 0) && (
                  <text
                    x={xCenter}
                    y={height - padB / 2}
                    className="axis-x"
                    textAnchor="middle"
                  >
                    {p.weekNumber}
                  </text>
                )}
              </g>
            );
          })}

          {/* Tooltip */}
          {hoverIdx !== null && (() => {
            const p = points[hoverIdx]!;
            const t = triples[hoverIdx]!;
            const x = padL + colW * (hoverIdx + 0.5);
            const tipW = 180;
            const tipH = 88;
            const tipX = Math.min(width - padR - tipW, Math.max(padL, x - tipW / 2));
            const tipY = Math.max(8, yFor(t.actual) - tipH - 8);
            return (
              <g className="tooltip">
                <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="6" />
                <text x={tipX + 10} y={tipY + 18} className="tip-title">{p.label}</text>
                <text x={tipX + 10} y={tipY + 38} className="tip-line tip-actual">
                  ● {currentSeason}: {format(t.actual, metric)}
                </text>
                <text x={tipX + 10} y={tipY + 56} className="tip-line tip-last">
                  ● {previousSeason ?? 'LY'}: {format(t.last, metric)}
                </text>
                <text x={tipX + 10} y={tipY + 74} className="tip-line tip-target">
                  ● Objetivo: {format(t.target, metric)}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}
